import { ChangeDetectionStrategy, Component, ErrorHandler, Input, OnDestroy, OnInit, signal } from '@angular/core'

import { combineLatest, firstValueFrom, Observable, of, Subject } from 'rxjs'
import { map, switchMap, takeUntil } from 'rxjs/operators'

import { RaporttiType } from '@lemon/core-firebase/model/reports-shared'
import { RaporttiPdfResponse, RaporttiRequest, TilinpaatosLuetteloKirjanpidoista } from 'app/_jaettu-lemonator/model/kirjanpito'
import { KirjanpitoUriService } from 'app/_jaettu-lemonator/service/kirjanpito-uri.service'

import { MatSnackBar } from '@angular/material/snack-bar'
import { KopioijaPalvelu } from '@lemon/core-firebase/service/kopioija.service'
import { DateService } from '@lemon/core/service/date.service'
import { AsiakasService } from 'app/_angular/service/asiakas/asiakas.service'
import { DebugService } from 'app/_angular/service/debug.service'
import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { KirjautunutKayttajaService } from 'app/_angular/service/kirjautunut-kayttaja.service'
import { LadataanService } from 'app/_jaettu-angular/service/ladataan.service'
import { TimestampService } from 'app/_jaettu-angular/service/timestamp-service'
import { Tilikausi } from 'app/_jaettu-lemonator/model/asiakas'

import { animate, state, style, transition, trigger } from '@angular/animations'
import { form } from '@angular/forms/signals'
import { asetaArvo } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { lemonShare } from 'app/_jaettu-angular/_rxjs/lemon-share.operator'
import { FileSaverService } from 'app/_jaettu-angular/service/file-saver'
import { TilinpaatosStatus } from 'app/_jaettu-lemonator/model/tilinpaatos'

interface LuetteloLomake {
  lisatiedot: string
}

@Component({
  standalone: false,
  selector: '[app-kirjanpito-tilinpaatos-luettelo]',
  templateUrl: './luettelo.component.html',
  styleUrls: ['./luettelo.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    // the fade-in/fade-out animation.
    trigger('slowInQuickOut', [
      // the "in" style determines the "resting" state of the element when it is visible.
      state('in', style({ opacity: 1 })),

      // fade in when created. this could also be written as transition('void => *')
      transition(':enter', [style({ opacity: 0 }), animate('600ms 3s')]),

      // fade out when destroyed. this could also be written as transition('* => void')
      transition(':leave', animate(100, style({ opacity: 0 }))),
    ]),
    // the fade-in/fade-out animation.
    trigger('quickInSlowOut', [
      // the "in" style determines the "resting" state of the element when it is visible.
      state('in', style({ opacity: 1 })),

      // fade in when created. this could also be written as transition('void => *')
      transition(':enter', [style({ opacity: 0 }), animate(100)]),

      // fade out when destroyed. this could also be written as transition('* => void')
      transition(':leave', animate(600, style({ opacity: 0 }))),
    ]),
  ],
})
export class KirjanpitoTilinpaatosLuetteloComponent implements OnInit, OnDestroy {
  @Input() selectedTilikausiObservable: Observable<Tilikausi>
  @Input() paivitaArvotHiljaisestiObservable: Observable<number>
  @Input() tilinpaatosStatusObservable: Observable<TilinpaatosStatus>

  private _ngUnsubscribe: Subject<void> = new Subject<void>()

  kirjanpitajaOnDevaajaObservable: Observable<boolean> = this._kirjautunutKayttajaService.kirjanpitajaOnDevaajaObservable

  luetteloKirjanpidoistaUritObservable: Observable<{ uri: string; display: string }>
  luetteloKirjanpidoistaPdfObservable: Observable<string>
  luetteloKirjanpidoistaDataObservable: Observable<TilinpaatosLuetteloKirjanpidoista>
  luetteloKirjanpidoistaObservable: Observable<{ data: TilinpaatosLuetteloKirjanpidoista; lukittu: boolean }>

  lomake = form(signal<LuetteloLomake>({ lisatiedot: '' }))

  constructor(
    private _firebaseLemonator: FirebaseLemonator,
    private _dateService: DateService,
    private _asiakasService: AsiakasService,
    private _kirjanpitoUriService: KirjanpitoUriService,
    private _errorHandler: ErrorHandler,
    private _copyService: KopioijaPalvelu,
    private _ladataanService: LadataanService,
    private _snackbar: MatSnackBar,
    private _debugService: DebugService,
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
    private _timestampService: TimestampService,
    private _fileSaverService: FileSaverService,
  ) {}

  ngOnInit() {
    this.luetteloKirjanpidoistaUritObservable = combineLatest([this._asiakasService.nykyinenAsiakasAvainObservable, this.selectedTilikausiObservable]).pipe(
      takeUntil(this._ngUnsubscribe),
      map(([asiakas, tilikausi]) => {
        if (asiakas && tilikausi) {
          const uri = this._kirjanpitoUriService.annaTilinpaatosLuetteloKirjanpidoistaUriAvaimella(asiakas.avain, tilikausi.avain)
          return { uri: this._debugService.createFirestoreLink(uri), display: uri }
        }
        return null
      }),
    )

    this.luetteloKirjanpidoistaDataObservable = combineLatest([this._asiakasService.nykyinenAsiakasAvainObservable, this.selectedTilikausiObservable]).pipe(
      takeUntil(this._ngUnsubscribe),
      switchMap(([asiakas, tilikausi]) => {
        if (asiakas && tilikausi) {
          const uri = this._kirjanpitoUriService.annaTilinpaatosLuetteloKirjanpidoistaUriAvaimella(asiakas.avain, tilikausi.avain)
          return this._firebaseLemonator.firestoreDoc<TilinpaatosLuetteloKirjanpidoista>(uri).listen()
        }
        return of<TilinpaatosLuetteloKirjanpidoista>(null)
      }),
      lemonShare(),
    )

    this.luetteloKirjanpidoistaPdfObservable = combineLatest([
      this._asiakasService.nykyinenAsiakasAvainObservable,
      this.selectedTilikausiObservable,
      this.paivitaArvotHiljaisestiObservable, // Kirjaukset muuttuneet, PDF:n tulisi päivittyä
      this.luetteloKirjanpidoistaDataObservable, // Luetteloon liittyvää dataa muokattu, PDF:n tulisi päivittyä
    ]).pipe(
      takeUntil(this._ngUnsubscribe),
      switchMap(([nykyinenAsiakas, tilikausi]) => {
        const haku: RaporttiRequest = {
          a: nykyinenAsiakas.avain,
          w: RaporttiType.LUETTELO_KIRJANPIDOISTA_JA_AINEISTOISTA,
          s: this._dateService.localDateToNumber(tilikausi.alkaa),
          e: this._dateService.localDateToNumber(tilikausi.loppuu),
          l: this._dateService.dateToLocalDateTime(new Date()),
          k: 'fi',
          ta: tilikausi.avain,
          datasource: 'nippudokumentit',
        }

        return this._firebaseLemonator.functionsCall<RaporttiRequest, RaporttiPdfResponse>('kirjanpitoRaportitPdf', haku).then(data => {
          if (data.e) {
            switch (data.e) {
              case 'unknown-report': {
                this._snackbar.open('Raporttia ei ole vielä implementoitu.', 'OK')
                break
              }
              case 'view-not-allowed': {
                this._snackbar.open('Käyttäjälla ei ole oikeutta nähdä tätä asiakasta.', 'OK')
                break
              }
              default: {
                this._snackbar.open('Tietojen lataaminen epäonnistui. (' + data.e + ')', 'OK')
              }
            }
          } else {
            if (data.base64File) {
              return 'data:application/pdf;base64,' + data.base64File
            } else {
              this._snackbar.open('PDF:n lataaminen esikatseluun epäonnistui.')
            }
          }
          return null
        })
      }),
      lemonShare(),
    )

    this.luetteloKirjanpidoistaObservable = combineLatest([this.luetteloKirjanpidoistaDataObservable, this.tilinpaatosStatusObservable]).pipe(
      takeUntil(this._ngUnsubscribe),
      map(([data, status]) => {
        return {
          data: data,
          lukittu: !!status?.rekisteroity,
        }
      }),
    )

    this.luetteloKirjanpidoistaDataObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(data => {
      this.alustaForm(data)
    })

    this.alustaForm(null)
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  alustaForm(data: TilinpaatosLuetteloKirjanpidoista) {
    asetaArvo(this.lomake, {
      lisatiedot: data?.lisatiedot || '',
    })
  }

  async downloadPdf() {
    this._ladataanService.aloitaLataaminen()

    const nykyinenAsiakas = await firstValueFrom(this._asiakasService.nykyinenAsiakasObservable)
    const tilikausi = await firstValueFrom(this.selectedTilikausiObservable)

    const haku: RaporttiRequest = {
      a: nykyinenAsiakas.avain,
      w: RaporttiType.LUETTELO_KIRJANPIDOISTA_JA_AINEISTOISTA,
      s: this._dateService.localDateToNumber(tilikausi.alkaa),
      e: this._dateService.localDateToNumber(tilikausi.loppuu),
      l: this._dateService.dateToLocalDateTime(new Date()),
      k: 'fi',
      ta: tilikausi.avain,
      datasource: 'nippudokumentit',
    }

    return this._firebaseLemonator.functionsCall<RaporttiRequest, RaporttiPdfResponse>('kirjanpitoRaportitPdf', haku).then(data => {
      if (data.e) {
        switch (data.e) {
          case 'unknown-report': {
            this._snackbar.open('Raporttia ei ole vielä implementoitu.', 'OK')
            break
          }
          case 'view-not-allowed': {
            this._snackbar.open('Käyttäjälla ei ole oikeutta nähdä tätä asiakasta.', 'OK')
            break
          }
          default: {
            this._snackbar.open('Tietojen lataaminen epäonnistui. (' + data.e + ')', 'OK')
          }
        }
      } else {
        if (data.base64File) {
          this._fileSaverService.saveBase64As(data.base64File, data.fileName, 'pdf')
        } else {
          this._snackbar.open('PDF:n lataaminen epäonnistui.')
        }
      }
      this._ladataanService.lopetaLataaminen()
    })
  }

  async tallenna() {
    this._ladataanService.aloitaLataaminen()

    const asiakasPromise = firstValueFrom(this._asiakasService.nykyinenAsiakasObservable)
    const tilikausiPromise = firstValueFrom(this.selectedTilikausiObservable)
    const kirjanpitajaPromise = firstValueFrom(this._kirjautunutKayttajaService.kirjanpitajanTiedotObservable)

    const [asiakas, tilikausi, kirjanpitaja] = await Promise.all([asiakasPromise, tilikausiPromise, kirjanpitajaPromise])

    if (!asiakas || !tilikausi || !kirjanpitaja) {
      this._ladataanService.lopetaLataaminen()
      this._snackbar.open('Lisätietojen tallentamisen aikana tapahtui virhe. Ole hyvä ja yritä uudelleen.', 'OK', { duration: 5000, verticalPosition: 'bottom' })
      return
    }

    const tallennettava: TilinpaatosLuetteloKirjanpidoista = {
      avain: tilikausi.avain,
      luoja: kirjanpitaja.uid,
      luotu: this._timestampService.now(),
      lisatiedot: this.lomake.lisatiedot().value()?.trim() || null,
    }

    const uri = this._kirjanpitoUriService.annaTilinpaatosLuetteloKirjanpidoistaUriAvaimella(asiakas.avain, tilikausi.avain)
    const batch = this._firebaseLemonator.firestoreBatch()

    batch.set(uri, tallennettava, { merge: true })

    const historiakopio: TilinpaatosLuetteloKirjanpidoista = this._copyService.cloneObjectDeep(tallennettava)
    historiakopio.avain = this._firebaseLemonator.firestoreCreateId()
    const historiaUri = this._kirjanpitoUriService.annaTilinpaatosLuetteloKirjanpidoistaHistoriaUriAvaimella(asiakas.avain, tilikausi, historiakopio.avain)
    batch.set(historiaUri, historiakopio)

    try {
      await batch.commit()
      this._ladataanService.lopetaLataaminen()
      this._snackbar.open('Lisätiedot tallennettiin onnistuneesti.', 'OK', { duration: 5000, verticalPosition: 'bottom' })
    } catch (error) {
      this._ladataanService.lopetaLataaminen()
      this._errorHandler.handleError(error)
      this._snackbar.open('Lisätietojen tallentamisen aikana tapahtui virhe. Ole hyvä ja yritä uudelleen.', 'OK', { duration: 5000, verticalPosition: 'bottom' })
    }
  }
}
